import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLocalHoldingsBranch } from "../../../resource/LocalHoldingsBranch";
import { ReactNode, useMemo } from "react";
import { useItemAvailability } from "../../../resource/ItemAvailability";
import React from "react";
import { Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { LSResultItemAvailabilitySkeleton } from "./LSResultItemSkeleton";
import { useVisibleHoldings } from "../../../resource/useVisibleHoldings";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";

function AvailabilityStatement({
  availableStatus,
  severity,
  count,
  locationPreposition,
  locationName,
  dense = false,
}: {
  dense?: boolean;
  count?: number;
  availableStatus: string;
  severity: "error" | "info" | "success";
  locationPreposition?: string;
  locationName?: string;
}) {
  const { translate } = useLanguage();

  const at = locationPreposition ?? translate("availability_at");
  const availabilityText = count
    ? `${count} ${availableStatus}`
    : availableStatus;
  const tooltip =
    dense &&
    `${availabilityText}${locationName ? ` ${at} ${locationName}` : ""}`;

  if (dense) {
    return (
      <Tooltip title={tooltip}>
        <Typography
          tabIndex={0}
          component="span"
          color={severity}
          fontWeight="bold"
        >
          {availabilityText}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Typography component="span">
      <Typography component="span" color={severity} fontWeight="bold">
        {availabilityText}
      </Typography>
      {locationName && (
        <>
          <Typography component="span"> {at} </Typography>
          <Typography component="span" fontWeight="bold">
            {locationName}
          </Typography>
        </>
      )}
    </Typography>
  );
}

interface LSResultItemAvailabilityProps {
  resource: ResourceDWR | ResourceView;
  dense?: boolean;
  reserves?: boolean;
}
export const LSResultItemAvailability = ({
  resource,
  dense = false,
  reserves = false,
}: LSResultItemAvailabilityProps) => {
  const { translate } = useLanguage();
  const { branchLimits, selectedBranchIds, displayInCopiesBranchIds } =
    useBranchLimits();
  const { branchIdentifier: localHoldingsBranch } = useLocalHoldingsBranch();
  const visibleHoldings = useVisibleHoldings(resource, reserves);

  //   sync TODO move shelfLocationBranch to a hook or context
  const shelfLocationBranch = useMemo(() => {
    if ((selectedBranchIds ?? []).length === 1) return selectedBranchIds[0];
    if (localHoldingsBranch) return localHoldingsBranch; // ignored if selectedBranchIds.length > 0
    return null;
  }, [localHoldingsBranch, selectedBranchIds]);

  //   sync
  const holdings = useMemo(() => {
    if (selectedBranchIds?.length) {
      // limit to copies at selected locations
      // this is likely already happening via the /search endpoint
      return visibleHoldings.filter((hi) =>
        selectedBranchIds.includes(hi.branchIdentifier)
      );
    }

    // TODO this seems bad
    // Instead of silently resolving this branch we should select it in the filter (This Location or Your Location)
    // limit to copies at the resolved shelf location
    if (shelfLocationBranch) {
      return visibleHoldings.filter(
        (hi) => hi.branchIdentifier === shelfLocationBranch
      );
    }

    // limit to copies displayed by the config
    // this is hopefully already happening via the /search endpoint
    return visibleHoldings;
  }, [visibleHoldings, shelfLocationBranch]);

  const shelfLocationBranchName = useMemo(() => {
    const branchLimit = (branchLimits || []).find(
      (bl) => bl.identifier === shelfLocationBranch
    );
    return branchLimit ? branchLimit.displayName : null;
  }, [shelfLocationBranch, branchLimits]);

  //   async
  const { itemAvailability, ready } = useItemAvailability(holdings, resource);

  const viewLink = useMemo(() => {
    if (displayInCopiesBranchIds.length === 1) {
      // single branches don't display a link
      return null;
    }

    const linkText =
      selectedBranchIds.length > 0
        ? translate("availability_viewSelectedLocations")
        : shelfLocationBranch
        ? translate("availability_checkOtherLocations")
        : translate("availability_viewAllLocations");

    return (
      <>
        {" ("}
        <Tooltip title={linkText}>
          <Link to={`/resource/${resource.id}/copies`}>
            {dense ? translate("view") : linkText}
          </Link>
        </Tooltip>
        {")"}
      </>
    );
  }, [
    selectedBranchIds,
    translate,
    dense,
    resource.id,
    shelfLocationBranch,
    displayInCopiesBranchIds,
  ]);

  const whereText = useMemo(() => {
    if (selectedBranchIds.length > 0) return null; // we don't display location names when filtering
    if (shelfLocationBranch) {
      return shelfLocationBranchName;
    }
    return translate("availability_allLocations");
  }, [
    selectedBranchIds,
    shelfLocationBranch,
    shelfLocationBranchName,
    translate,
  ]);

  const availabilityLink: ReactNode = useMemo(() => {
    if (!ready) {
      return null;
    }
    const itemAvailabilities = Array.from(itemAvailability.values());

    // no copies
    if (itemAvailabilities.length === 0 && resource.serial) {
      return (
        <Typography>
          <AvailabilityStatement
            dense={dense}
            availableStatus={translate("serialMessage")}
            severity={"info"}
          />
        </Typography>
      );
    }

    if (itemAvailabilities.length === 0 && !resource.serial) {
      return (
        <Typography>
          <AvailabilityStatement
            dense={dense}
            availableStatus={translate("noCopies")}
            severity={"info"}
            locationName={whereText}
          />
          {viewLink}
        </Typography>
      );
    }

    const countAvailable = itemAvailabilities.filter(
      (ia) => ia.available && !ia.nonCirculating && !ia.onOrder
    ).length;
    if (countAvailable > 0) {
      return (
        <Typography>
          <AvailabilityStatement
            dense={dense}
            count={countAvailable}
            availableStatus={translate("available")}
            severity={"success"}
            locationName={whereText}
          />
          {viewLink}
        </Typography>
      );
    }
    const countNonCirculating = itemAvailabilities.filter(
      (ia) => ia.nonCirculating
    ).length;
    if (countNonCirculating > 0) {
      return (
        <Typography>
          <AvailabilityStatement
            dense={dense}
            count={countNonCirculating}
            availableStatus={translate("nonCirculating")}
            severity={"success"}
            locationName={whereText}
          />
          {viewLink}
        </Typography>
      );
    }

    const onOrderCount = itemAvailabilities.filter((ia) => ia.onOrder).length;
    if (onOrderCount > 0) {
      return (
        <Typography>
          <AvailabilityStatement
            dense={dense}
            count={onOrderCount}
            availableStatus={translate("onOrder")}
            severity={"error"}
            locationName={whereText}
          />
          {viewLink}
        </Typography>
      );
    }
    return (
      <Typography>
        <AvailabilityStatement
          dense={dense}
          availableStatus={translate("notAvailable")}
          severity={"error"}
          locationName={whereText}
        />
        {viewLink}
      </Typography>
    );
  }, [ready, itemAvailability, shelfLocationBranch]);

  //   reserve and serial have different handling apparently
  if (ready && !!availabilityLink) {
    return availabilityLink;
  }
  return <LSResultItemAvailabilitySkeleton />;
};
